import { z } from 'zod';
/**
 * Hợp đồng xác thực — docs/security.md §2.
 *
 * Refresh token **không** xuất hiện trong bất kỳ schema nào ở đây: nó sống
 * trong cookie `httpOnly` và JavaScript không được đọc tới. Đưa nó vào response
 * body là mở đường cho XSS lấy được — xem `AuthTokens`.
 */
/**
 * 12 ký tự theo khuyến nghị OWASP hiện hành. Trần 200 để một chuỗi vài megabyte
 * không biến mỗi lần đăng nhập thành một lời mời DoS: Argon2id cố tình tốn CPU.
 */
export const Password = z
    .string()
    .min(12, 'Mật khẩu phải có ít nhất 12 ký tự')
    .max(200, 'Mật khẩu quá dài');
export const Email = z.email('Email không hợp lệ').max(320).toLowerCase().trim();
/**
 * Đăng ký tạo **cả tổ chức lẫn người dùng đầu tiên**, và người đó là
 * `ORG_ADMIN`. Chưa có tổ chức thì không có chỗ nào để mời người vào — luồng
 * mời thành viên là việc của `APM-403`.
 */
export const RegisterInput = z.object({
    orgName: z.string().trim().min(1, 'Tên tổ chức là bắt buộc').max(200),
    email: Email,
    password: Password,
    displayName: z.string().trim().min(1, 'Tên hiển thị là bắt buộc').max(200),
});
/**
 * Hoàn tất đăng ký sau khi Google xác minh email — APM-GAUTH-a.
 *
 * Không có `email`: email đến từ **phiếu**, không từ client. Nhận email do
 * client gửi thì bất kỳ ai cầm một phiếu hợp lệ cũng tạo được tài khoản mang
 * địa chỉ của người khác — và phiếu ấy là thứ phát ra sau mỗi lần bấm Google.
 *
 * Không có `ticket`: phiếu nằm trong cookie `HttpOnly` (APM-GAUTH-d), nên
 * JavaScript của trang không đọc được nó và nó không đi vào lịch sử duyệt web
 * hay header `Referer` như hồi còn ở URL.
 *
 * Không có `password`: tài khoản này đăng nhập bằng Google. `password_hash`
 * nullable, và đường đăng nhập mật khẩu đã chặn đúng tài khoản không có mật
 * khẩu. Ép một mật khẩu ngẫu nhiên vào đây là tạo một bí mật không ai biết,
 * không ai đổi được, mà vẫn nằm trong bảng.
 *
 * ## Ba lựa chọn — APM-PSPACE-m (docs/plan-personal-space.md §0.3)
 *
 * Màn "Bạn cần thuộc một tổ chức" hiện ba thẻ cho người mới đến bằng Google không link,
 * link tổ chức **cần duyệt**, hoặc link cộng tác viên:
 *
 * - `personal` — tổ chức chỉ có mình tôi (không gian cá nhân, tính năng hạn chế).
 * - `full` — tổ chức đầy đủ, người tạo là quản trị; cần `orgName`.
 * - `guest_only` — thẻ 3, "chỉ vào nơi được mời". Với link cộng tác viên: tài khoản ra đời
 *   với nhà là **tổ chức của dự án** (`GUEST`). Với link tổ chức cần duyệt: **không** tạo
 *   tài khoản, chỉ gửi đơn theo email rồi ở lại phòng chờ (kết cục `OrgJoinPending`).
 *   Không link thì thẻ 3 là phòng chờ thuần và **không** gọi endpoint này.
 *
 * Link khách hàng (`clientInviteToken`) và link tổ chức **không** cần duyệt không có ba thẻ:
 * không gửi `choice` (link tổ chức thì server bỏ qua nó nếu link không cần duyệt).
 */
export const OnboardingChoice = z.enum(['personal', 'full', 'guest_only']);
export const CompleteOnboardingInput = z
    .object({
    displayName: z.string().trim().min(1, 'Tên hiển thị là bắt buộc').max(200),
    /** Thẻ đã chọn ở màn ba lựa chọn — APM-PSPACE-m. */
    choice: OnboardingChoice.optional(),
    /** Tên tổ chức đầy đủ — chỉ đi cùng `choice: 'full'`. */
    orgName: z.string().trim().min(1).max(200).optional(),
    /** Vào tổ chức đã mời mình (link `/join`). */
    inviteToken: z.string().min(1).max(200).optional(),
    /**
     * Link mời **cộng tác viên ngoài** vào một dự án — APM-GAUTH-e. Tạo tài khoản + tư cách
     * `GUEST` + đơn chờ PM duyệt; nhà theo `choice` (APM-PSPACE-m).
     */
    guestInviteToken: z.string().trim().min(1).max(200).optional(),
    /**
     * Link mời **khách hàng** vào một dự án — tài khoản `CLIENT` + đơn chờ duyệt. Nhà vẫn là
     * tổ chức của link (để đăng nhập mở `/portal`); không có ba thẻ.
     */
    clientInviteToken: z.string().trim().min(1).max(200).optional(),
})
    .superRefine((value, ctx) => {
    const links = [value.inviteToken, value.guestInviteToken, value.clientInviteToken].filter((link) => link !== undefined);
    if (links.length > 1) {
        ctx.addIssue({ code: 'custom', path: ['inviteToken'], message: 'Chỉ dùng một link mời' });
    }
    // Tên tổ chức đi đúng với thẻ "đầy đủ", và ngược lại: đoán hộ ở đây nghĩa là lập tổ
    // chức cho người vừa bấm một link mời.
    if ((value.choice === 'full') !== (value.orgName !== undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['orgName'],
            message: 'Tên tổ chức đi cùng lựa chọn "Tổ chức mới đầy đủ"',
        });
    }
    if (value.clientInviteToken !== undefined && value.choice !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['choice'],
            message: 'Link khách hàng không có lựa chọn tổ chức',
        });
    }
    if (value.guestInviteToken !== undefined && value.choice === undefined) {
        ctx.addIssue({ code: 'custom', path: ['choice'], message: 'Chọn một trong ba cách bắt đầu' });
    }
    if (links.length === 0 && (value.choice === undefined || value.choice === 'guest_only')) {
        // Không link: thẻ 3 là phòng chờ (không gọi endpoint này), nên chỉ còn 1 hoặc 2.
        ctx.addIssue({ code: 'custom', path: ['choice'], message: 'Chọn một trong ba cách bắt đầu' });
    }
});
/** Những gì trang onboarding hiện được, đọc từ phiếu. */
export const OnboardingTicketView = z.object({
    email: z.email(),
    /** Tên Google gợi ý — điền sẵn, không khoá. */
    suggestedName: z.string().nullable(),
    /**
     * Phòng chờ (thẻ 3, APM-GAUTH-e → APM-PSPACE-m). Email này **đã có** tài khoản dùng được
     * (quản trị vừa mời bằng email, hoặc vừa duyệt đơn): `POST /auth/onboarding/enter` mở phiên
     * ngay, không phải bấm Google lại.
     */
    accountReady: z.boolean().default(false),
    /** Đơn xin vào tổ chức của email này còn chờ hoặc vừa bị từ chối (30 ngày). */
    joinRequests: z
        .array(z.object({
        orgName: z.string(),
        status: z.enum(['pending', 'declined']),
        createdAt: z.iso.datetime(),
    }))
        .default([]),
});
export const LoginInput = z.object({
    email: Email,
    password: z.string().min(1, 'Mật khẩu là bắt buộc').max(200),
});
export const ChangePasswordInput = z.object({
    currentPassword: z.string().min(1).max(200),
    newPassword: Password,
});
export const ForgotPasswordInput = z.object({ email: Email });
export const ResetPasswordInput = z.object({
    token: z.string().min(1).max(200),
    newPassword: Password,
});
/**
 * Vai trò cấp tổ chức — docs/security.md §3, APM-ORG-a.
 *
 * Bốn vai trong một tổ chức, cộng `SUPER_ADMIN` đứng ngoài:
 *
 * - `SUPER_ADMIN` — tài khoản kỹ thuật vận hành hệ thống. Không phải một vị trí
 *   trong tổ chức nào, nên nó không xuất hiện ở màn hình gán vai trò.
 * - `ORG_ADMIN` — admin của tổ chức: tạo tài khoản và gán ba vai dưới đây.
 * - `DIRECTOR` — đọc **mọi** dự án và hiệu suất của **mọi** người trong tổ
 *   chức. Không quản trị hạ tầng (không `ai_config:manage`, không
 *   `storage_config:manage`): nhìn toàn cảnh để ra quyết định và vận hành hệ
 *   thống là hai việc khác nhau.
 * - `LEADER` — có nhân sự dưới quyền. Tự mở được dự án.
 * - `EMPLOYEE` — mở dự án thì quản lý trực tiếp phải duyệt.
 * - `CLIENT` — **người ngoài tổ chức**, xem docs/plan-issue-management.md §8.2.
 * - `GUEST` — cộng tác viên **ngoài tổ chức**, làm task được giao trong dự án
 *   được mời — docs/plan-guest-collaborator.md.
 *
 * ## ⚠️ `LEADER` là vai trò, `line_manager_id` là quan hệ — cần cả hai
 *
 * Vai trò trả lời "người này có tự mở dự án được không". Quan hệ trả lời "ai
 * duyệt cho người kia". Bỏ vai trò thì phải suy từ "có ai dưới quyền không", và
 * một trưởng nhóm vừa nhận việc, chưa ai được gán vào, sẽ phải xin phép. Bỏ
 * quan hệ thì biết được phải duyệt mà không biết gửi cho ai.
 */
/**
 * ## ⚠️ `CLIENT` không phải một bậc dưới `EMPLOYEE` — nó ở ngoài thang
 *
 * Bốn vai trên là **nhân sự**: họ có lịch làm việc, có trần năng lực, có hồ sơ
 * hiệu suất, và họ xuất hiện trong danh bạ, sơ đồ tổ chức, bảng phân bổ. `CLIENT`
 * không có thứ nào trong số đó — họ là khách hàng đăng nhập để báo và theo dõi
 * vấn đề của chính mình (APM-ISSUE-l).
 *
 * Họ vẫn cần một hàng `org_membership` vì `toAuthenticatedUser` từ chối mở phiên
 * cho người không thuộc tổ chức của phiên — và nó từ chối **có chủ đích**. Nên
 * cửa giữ họ ra khỏi các bảng nhân sự không nằm ở xác thực mà ở `staffOnly()`,
 * một chỗ duy nhất có test kiến trúc canh.
 *
 * ⚠️ Đặt **cuối** danh sách, không phải theo thứ tự đọc: enum này đi vào một cột
 * `ENUM` của MySQL, nơi giá trị được lưu bằng **chỉ số thứ tự**. Chèn vào giữa
 * sẽ dịch mọi giá trị phía sau và đọc sai toàn bộ dữ liệu đang có — cùng bài học
 * đã ghi ở `RaidType.ASSUMPTION`.
 */
export const OrgRole = z.enum([
    'SUPER_ADMIN',
    'ORG_ADMIN',
    'DIRECTOR',
    'LEADER',
    'EMPLOYEE',
    'CLIENT',
    /**
     * Cộng tác viên ngoài tổ chức — APM-GUEST-a. Ngoài thang nhân sự như `CLIENT`,
     * nhưng **làm việc** trong dự án (task được giao / do mình tạo) thay vì chỉ báo
     * issue. Cuối danh sách, cùng lý do với `CLIENT`.
     */
    'GUEST',
]);
/**
 * Ba vai mà `ORG_ADMIN` gán được cho người trong tổ chức.
 *
 * `SUPER_ADMIN` và `ORG_ADMIN` cố ý vắng mặt: vai đầu không phải vị trí trong
 * tổ chức, vai sau là quyền tự nhân bản — một `ORG_ADMIN` phong thêm
 * `ORG_ADMIN` thì danh sách người quản trị không còn ai kiểm soát được.
 */
export const ASSIGNABLE_ORG_ROLES = ['DIRECTOR', 'LEADER', 'EMPLOYEE'];
/**
 * Vị trí của người vừa **tạo** tổ chức (đăng ký, onboarding Google, tạo tổ chức
 * mới) — APM-OADM-a. Họ là quản trị (`isOrgAdmin = true`) và kiêm LEADER: tự mở
 * dự án, đứng tên duyệt được, nhưng không tự có quyền xem hiệu suất toàn tổ
 * chức — thứ quản trị khác phải cấp bằng cách đổi vị trí sang `DIRECTOR`.
 * Cùng giá trị migration đã gán cho mọi quản trị có từ trước.
 */
export const ORG_ADMIN_POSITION = 'LEADER';
/**
 * Vai trò được phép đứng tên duyệt dự án cho người khác — APM-ORG-a.
 *
 * Dùng ở hai chỗ và phải giống nhau: lúc gán quản lý trực tiếp, và lúc quyết
 * xem một dự án mới có cần duyệt không. Lệch nhau thì gán được một quản lý mà
 * hệ thống không chịu gửi đơn tới.
 */
export const MANAGING_ORG_ROLES = ['SUPER_ADMIN', 'ORG_ADMIN', 'DIRECTOR', 'LEADER'];
/**
 * Người này có đứng tên quản lý / duyệt được không — APM-OADM-a.
 *
 * Quản trị tổ chức **luôn** được, bất kể vị trí kiêm: họ đã có `project:update`
 * ở mọi dự án, nên bắt họ xin duyệt (hay cấm họ duyệt) chỉ là thủ tục không
 * chặn được gì. Mọi chỗ trước đây so `MANAGING_ORG_ROLES.includes(orgRole)` đi
 * qua hàm này, để cờ quản trị không bị quên ở một nửa số chỗ.
 *
 * `'ORG_ADMIN'` còn nằm trong `MANAGING_ORG_ROLES` chỉ để đọc được token cũ; từ
 * APM-OADM-a không hàng nào mang vai ấy nữa.
 */
export function isManagingMember(member) {
    return (member.isOrgAdmin === true || MANAGING_ORG_ROLES.includes(member.orgRole));
}
export const AuthenticatedUser = z.object({
    id: z.uuid(),
    orgId: z.uuid(),
    /**
     * Tên tổ chức đang đăng nhập — APM-AUTH-h.
     *
     * Một phiên chỉ nhìn thấy **đúng một** tổ chức (`scopeFilter` ghim `orgId`),
     * nên đây không phải để chọn giữa nhiều tổ chức mà để trả lời câu "tôi đang
     * xem dữ liệu của ai". Nghe thừa cho tới lúc hai tổ chức có dự án **trùng
     * tên** — và bộ dữ liệu mẫu tạo ra đúng cảnh ấy: cùng mã `PAY`, cùng tên,
     * cùng số việc, khác dữ liệu. Không có nhãn thì hai màn hình giống hệt nhau
     * và không gì nói ra chúng khác nhau.
     */
    orgName: z.string(),
    email: z.string(),
    displayName: z.string(),
    /**
     * **Vị trí** trong tổ chức — Giám đốc / Leader / Nhân viên (hoặc `SUPER_ADMIN`,
     * `CLIENT`). Từ APM-OADM-a không bao giờ là `ORG_ADMIN`: quản trị là `isOrgAdmin`.
     */
    orgRole: OrgRole,
    /**
     * Quản trị tổ chức — APM-OADM-a. Cờ, không phải vị trí: một người vừa quản trị
     * vừa là Giám đốc thì `isOrgAdmin = true, orgRole = 'DIRECTOR'`, và quyền là
     * hợp của cả hai.
     */
    isOrgAdmin: z.boolean(),
    /**
     * Tổ chức của phiên là **không gian cá nhân** — APM-PSPACE-a (docs/plan-personal-space.md §3.1).
     * Giao diện ẩn tính năng bị chặn và hiện tên bằng i18n thay cho `orgName` (chữ dự phòng).
     */
    isPersonal: z.boolean(),
});
/**
 * Chỉ access token đi qua body. Frontend giữ nó **trong memory**, không phải
 * `localStorage` — XSS đọc được `localStorage`, còn biến trong module thì phải
 * chạy được code trong ngữ cảnh đó mới lấy được.
 */
export const AuthTokens = z.object({
    accessToken: z.string(),
    /** Giây, để FE hẹn giờ refresh trước khi hết hạn. */
    expiresIn: z.number().int().positive(),
    user: AuthenticatedUser,
});
/**
 * Một tài khoản trưng bày trên trang đăng nhập — APM-1318a.
 *
 * Chỉ chứa thứ cần để **chọn** một vai: không email thật, không quyền chi tiết,
 * không id tổ chức. Đây là dữ liệu của một endpoint công khai, nên mỗi trường
 * thêm vào là một trường ai cũng đọc được.
 */
/**
 * Vai mà một tài khoản demo **trưng bày** — trang đăng nhập chọn nhãn và biểu
 * tượng theo nó.
 *
 * Không suy ra được từ `orgRole` + `isOrgAdmin`: Leader và PM của kịch bản cùng
 * mang `LEADER` (PM là vai **trong dự án**, không phải cấp tổ chức), nên trang
 * đăng nhập từng phải đọc vai từ đuôi của `displayName`.
 */
export const DemoPersona = z.enum(['org_admin', 'director', 'leader', 'pm', 'member']);
export const DemoAccount = z.object({
    id: z.uuid(),
    persona: DemoPersona,
    displayName: z.string(),
    orgRole: OrgRole,
    /** Quản trị tổ chức — APM-OADM-a. Trang đăng nhập ghi nhãn theo nó. */
    isOrgAdmin: z.boolean(),
    /** Một câu nói người này mở app ra thì thấy gì. */
    description: z.string(),
});
/**
 * Bọc trong object thay vì trả thẳng mảng.
 *
 * Chưa dựng tổ chức demo là trạng thái **hợp lệ** (`accounts: []`), không phải
 * lỗi — trang đăng nhập chỉ ẩn mục đó đi. Và một endpoint trả về giá trị trần
 * thì mọi lần thêm trường sau này đều là breaking change.
 */
export const DemoAccountList = z.object({ accounts: z.array(DemoAccount) });
/**
 * Đăng nhập không mật khẩu vào một tài khoản trưng bày.
 *
 * ⚠️ Không nhận email: email là thứ người gọi tự gõ ra được, và một endpoint
 * không mật khẩu nhận email là một endpoint đoán được. `userId` phải tới từ
 * `GET /auth/demo-accounts`, và server vẫn kiểm lại rằng id đó thuộc một tổ
 * chức có `is_demo` — danh sách kia là tiện lợi, không phải cửa quyền.
 */
export const DemoLoginInput = z.object({ userId: z.uuid() });
/** Nội dung JWT access token. Giữ tối thiểu — token nào cũng có thể bị đọc. */
export const AccessTokenClaims = z.object({
    sub: z.uuid(),
    org: z.uuid(),
    role: OrgRole,
    /**
     * Cờ quản trị tổ chức — APM-OADM-a. Tuỳ chọn: access token cấp **trước**
     * APM-OADM-a không có nó và mang `role: 'ORG_ADMIN'` — `JwtAuthGuard` đọc
     * token ấy thành `adm = true, role = 'LEADER'`, đúng như migration đã làm với
     * hàng dữ liệu.
     */
    adm: z.boolean().optional(),
    iat: z.number(),
    exp: z.number(),
});
/**
 * Cấu hình đăng nhập Google — APM-AUTH-g.
 *
 * `clientSecret` chỉ đi **một chiều**: gửi lên để lưu, không bao giờ trả về.
 * Nên không có `GoogleOauthConfigView` — thứ đọc lại được là `GoogleOauthStatus`
 * dưới đây, và nó cố ý không mang secret.
 */
export const GoogleOauthConfigInput = z.object({
    clientId: z.string().trim().min(1).max(255),
    clientSecret: z.string().trim().min(1).max(255),
});
/**
 * `configured: false` là trạng thái **hợp lệ**, không phải lỗi — cùng quy ước
 * với `GET /ai/capabilities`: nút "Đăng nhập bằng Google" chỉ hiện khi
 * `configured`, và một bản cài không dùng Google vẫn chạy bình thường.
 */
export const GoogleOauthStatus = z.object({
    configured: z.boolean(),
    clientId: z.string().nullable(),
});
/**
 * Bản công khai của trạng thái trên: trang đăng nhập cần biết có hiện nút Google
 * hay không, mà người chưa đăng nhập thì không được đọc cấu hình.
 *
 * Chỉ một cờ, không kèm `clientId` — dù `clientId` vốn công khai (nó nằm ngay
 * trong URL sang Google), trả nó ở đây là mời người ta đọc cấu hình từ một
 * endpoint không cần quyền, và lần mở rộng sau sẽ rất dễ kèm theo thứ không nên.
 */
export const GoogleOauthAvailability = z.object({ configured: z.boolean() });
/**
 * Lọc tham số `?next=` — đường quay lại sau khi đăng nhập (APM-1317b).
 *
 * ## ⚠️ Vì sao không dùng thẳng giá trị trong URL
 *
 * Một `next` không lọc là lỗ **open redirect**: kẻ tấn công gửi
 * `/login?next=https://evil.example/login` cho nạn nhân. Nạn nhân đọc thấy
 * domain thật ở thanh địa chỉ, đăng nhập thật, rồi bị đẩy sang một trang login
 * giả trông y hệt và gõ mật khẩu lần thứ hai. Toàn bộ sức thuyết phục của cú
 * lừa đến từ việc chặng đầu là domain **đúng**.
 *
 * Nên chỉ nhận đường dẫn nội bộ, và kiểm bằng cách **dựng thử URL** thay vì so
 * chuỗi: trình duyệt có nhiều cách hiểu một chuỗi thành URL tuyệt đối hơn số
 * cách mà một biểu thức chính quy nghĩ tới (`//evil`, `\\/\\/evil`, `/\thttps://evil`,
 * ký tự điều khiển ở đầu…). Ai quyết định cuối cùng là `URL`, nên hãy hỏi nó.
 */
export function safeNext(next) {
    if (next === undefined || next === '' || next === '/')
        return undefined;
    // Origin giả: chỉ dùng để xem chuỗi có **thoát** khỏi origin hay không.
    const probe = 'https://apm.invalid';
    let url;
    try {
        url = new URL(next, probe);
    }
    catch {
        return undefined;
    }
    // Đổi origin nghĩa là chuỗi mang scheme hoặc host riêng ⇒ không phải nội bộ.
    if (url.origin !== probe)
        return undefined;
    // Trả về dạng đã chuẩn hoá của chính trình duyệt, không phải chuỗi thô: thứ
    // được kiểm và thứ được điều hướng tới phải là **một**.
    return `${url.pathname}${url.search}`;
}
/**
 * Một tổ chức mà người đang đăng nhập là thành viên — APM-ORG-c2.
 *
 * Không kèm bất kỳ dữ liệu nào của tổ chức ngoài tên và vai trò: danh sách này
 * hiện ở chỗ đổi tổ chức, và nó phải trả lời được cho **cả** tổ chức mà phiên
 * hiện tại không mở — tức nằm ngoài phạm vi mà `scopeFilter` cho phép đọc.
 */
export const MyOrganization = z.object({
    orgId: z.uuid(),
    orgName: z.string(),
    orgRole: OrgRole,
    /** Quản trị tổ chức — APM-OADM-a. */
    isOrgAdmin: z.boolean(),
    /** Tổ chức của phiên đang mở. Đúng một phần tử trong danh sách mang `true`. */
    isActive: z.boolean(),
    /** Không gian cá nhân — APM-PSPACE-a. Ô đổi tổ chức hiện tên bằng i18n khi `true`. */
    isPersonal: z.boolean(),
});
export const SwitchOrgInput = z.object({ orgId: z.uuid() });
/**
 * Mở thêm một tổ chức mới — APM-ORG-d.
 *
 * Chỉ có tên: mọi thứ khác (phương pháp, lịch làm việc, danh mục) là cấu hình
 * của tổ chức và sửa được sau. Hỏi hết ở bước tạo thì người dùng phải quyết
 * mười thứ trước khi thấy được cái gì.
 */
export const CreateOrgInput = z.object({
    name: z.string().trim().min(2).max(200),
});
/** Tạo link mời vào tổ chức — APM-ORG-e. */
export const CreateOrgInviteInput = z.object({
    /**
     * Vai được cấp khi nhận lời mời.
     *
     * ⚠️ Chỉ ba vai gán được; `ORG_ADMIN`/`SUPER_ADMIN` bị chặn ở tầng lệnh. Với
     * một **link** thì hậu quả nặng hơn hẳn một lượt gán tay — link đi qua chat,
     * qua email, qua ảnh chụp màn hình, và ai cầm được cũng dùng được.
     */
    orgRole: z.enum(ASSIGNABLE_ORG_ROLES).default('EMPLOYEE'),
    /**
     * Lời nhắn riêng của link — APM-ORGP-a. Hiện trên trang xem trước, trang
     * **công khai**: văn bản thuần, không HTML hay markdown.
     */
    message: z.string().trim().max(1000).nullable().optional(),
    /**
     * Người nhận phải chờ quản trị duyệt — APM-ORGJR-a. **Mặc định bật**: link đi
     * qua chat và ảnh chụp màn hình, nên "ai cầm link cũng vào được" phải là lựa
     * chọn có chủ ý, không phải thứ có sẵn.
     */
    requiresApproval: z.boolean().default(true),
});
export const OrgInviteView = z.object({
    id: z.uuid(),
    /** Chỉ có ngay sau khi tạo. Đọc lại link đã tạo là **không** được. */
    token: z.string().nullable(),
    createdAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    orgRole: OrgRole,
    /** Còn dùng được không: chưa thu hồi **và** chưa hết hạn. */
    isActive: z.boolean(),
    /**
     * Số người đã gia nhập bằng link này — con số **lịch sử**.
     *
     * Không giảm khi ai đó rời tổ chức: câu hỏi là "link này mời được bao nhiêu
     * người", không phải "còn lại bao nhiêu".
     */
    acceptedCount: z.number().int().nonnegative(),
    /** Lời nhắn riêng của link — APM-ORGP-a. */
    message: z.string().nullable(),
    /** Người nhận phải chờ duyệt — APM-ORGJR-a. */
    requiresApproval: z.boolean(),
});
/**
 * Kết cục "đã gửi đơn, chờ duyệt" của hai đường nhận lời mời tổ chức —
 * APM-ORGJR-a. Thay cho `AuthTokens`: chưa là thành viên thì chưa có phiên nào
 * ở tổ chức ấy để mở.
 */
export const OrgJoinPending = z.object({
    status: z.literal('pending'),
    orgName: z.string(),
});
export const OrgJoinRequestStatus = z.enum(['pending', 'accepted', 'declined']);
/** Một đơn xin vào tổ chức, nhìn từ phía quản trị — APM-ORGJR-a. */
export const OrgJoinRequestView = z.object({
    id: z.uuid(),
    email: z.string(),
    displayName: z.string(),
    orgRole: OrgRole,
    status: OrgJoinRequestStatus,
    note: z.string().nullable(),
    /**
     * `false` = người xin chưa có tài khoản (đăng nhập Google qua link): duyệt là
     * tạo tài khoản cho họ, và họ đăng nhập lại bằng Google để vào.
     */
    hasAccount: z.boolean(),
    /** Lời nhắn của link họ đã dùng — để quản trị nhận ra đơn đến từ đợt mời nào. */
    inviteMessage: z.string().nullable(),
    /**
     * Lúc link được tạo — APM-ORGJR-c. Cùng `inviteMessage` là cách quản trị nhận ra "qua link
     * nào": link không có tên, và lời nhắn thường để trống.
     */
    inviteCreatedAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
    decidedAt: z.iso.datetime().nullable(),
});
/** Đơn của chính mình — APM-ORGJR-a. */
export const MyOrgJoinRequestView = z.object({
    id: z.uuid(),
    orgName: z.string(),
    status: OrgJoinRequestStatus,
    note: z.string().nullable(),
    createdAt: z.iso.datetime(),
    decidedAt: z.iso.datetime().nullable(),
});
export const DeclineOrgJoinRequestInput = z.object({
    note: z.string().trim().max(500).nullable().optional(),
});
export const AcceptOrgInviteInput = z.object({ token: z.string().min(1) });
/**
 * URL chỉ nhận `http://`/`https://` — APM-ORGP-a.
 *
 * ⚠️ `website` và `logoUrl` hiện ra trên trang xem trước lời mời, trang
 * **công khai**. `z.url()` trần nhận cả `javascript:alert(1)` (đó là một URL
 * hợp lệ), và một `href` như thế là chạy script của người sửa hồ sơ trong
 * trình duyệt của người được mời.
 */
const HttpUrl = z.url({ protocol: /^https?$/ }).max(2048);
/**
 * Những gì người cầm link thấy **trước khi** bấm gia nhập — APM-ORG-e2,
 * mở rộng ở APM-ORGP-a.
 *
 * ⚠️ Endpoint trả nó đọc ngoài phạm vi `scopeFilter`: người gọi chưa thuộc tổ
 * chức, nên theo định nghĩa họ chưa được đọc gì của nó. Cái giá: ai cầm link
 * biết được hồ sơ, **quy mô** tổ chức, và **người mời**. Chấp nhận được vì đó
 * đúng là thứ cần để quyết định có gia nhập không — nhưng vì thế nó chỉ có
 * chừng này: không dự án, không số liệu, không tên thành viên nào khác.
 */
export const OrgInvitePreview = z.object({
    orgName: z.string(),
    description: z.string().nullable(),
    logoUrl: z.string().nullable(),
    website: z.string().nullable(),
    industry: z.string().nullable(),
    welcomeMessage: z.string().nullable(),
    /** Lời nhắn riêng của link. */
    message: z.string().nullable(),
    memberCount: z.number().int().nonnegative(),
    orgRole: OrgRole,
    /**
     * Người tạo link. `null` khi họ đã rời tổ chức hoặc bị khoá: lời mời vẫn
     * dùng được, nhưng tên một người không còn ở đó không được lộ ra nữa.
     */
    inviter: z.object({ name: z.string() }).nullable(),
    /** Bấm gia nhập là gửi đơn chờ duyệt, không vào ngay — APM-ORGJR-a. */
    requiresApproval: z.boolean(),
});
/**
 * Sửa hồ sơ tổ chức — APM-ORG-e2, dùng thật từ APM-ORGP-a.
 *
 * Trường vắng mặt = giữ nguyên; `null` (hay chuỗi rỗng sau khi cắt) = xoá.
 * Không có `slug`: nó "không đổi sau khi tạo", kể cả khi đổi tên.
 */
export const UpdateOrgProfileInput = z.object({
    name: z.string().trim().min(2).max(200).optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    logoUrl: HttpUrl.nullable().optional(),
    website: HttpUrl.nullable().optional(),
    industry: z.string().trim().max(200).nullable().optional(),
    welcomeMessage: z.string().trim().max(2000).nullable().optional(),
});
/** Hồ sơ tổ chức đọc về — APM-ORGP-a. Mọi thành viên đọc được. */
export const OrgProfile = z.object({
    name: z.string(),
    description: z.string().nullable(),
    logoUrl: z.string().nullable(),
    website: z.string().nullable(),
    industry: z.string().nullable(),
    welcomeMessage: z.string().nullable(),
});
//# sourceMappingURL=auth.js.map